import { generatePath, matchPath, Redirect } from "react-router-dom";
import { Location } from "history";
import React from "react";
import {
  ACCOUNT_EXTRA_DETAILS_SUFFIX,
  ADMIN_TRADING_CONFIGURATION_FORM_PATH,
  AFFILIATION_PATH,
  BACKTESTING_EXTRA_DETAILS_SUFFIX,
  BASE_BILLING_SETTINGS_PATH,
  BASE_BOTS_WITH_ACTION_PATH,
  BASE_CANCELLED_AGREEMENT_PATH,
  BASE_FAILED_AGREEMENT_PATH,
  BASE_PAID_AGREEMENT_PATH,
  BASE_PROCESSED_AGREEMENT_PATH,
  BASE_PUBLIC_BOT_PATH,
  BASE_PUBLIC_EDIT_BOT_PATH,
  BILLING_SETTINGS_PATH,
  BOT_EXTRA_DETAILS_SUFFIX,
  COIN_EXTRA_DETAILS_SUFFIX,
  CONNECT_TELEGRAM_PATH,
  CREATE_ACCOUNT_PATH,
  DISCORD_DISCONNECT_CONFIRMED_PATH,
  DISCORD_FOLLOW_SIGNAL_PATH,
  EXTERNAL_ACCOUNT_CONFIGURATION_PATH,
  GROUP_MANAGEMENT_PATH,
  GROUP_MANAGEMENT_TABS,
  LOGIN_PATH,
  MARKETPLACE_GROUP_SUFFIX,
  NEXT_URL_QUERY_PARAM,
  OAUTH_LOGIN_PATH,
  ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
  ONBOARDING_COPY_DCA_BOT_PATH,
  ONBOARDING_DCA_BOT_OVERVIEW_PATH,
  ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH,
  ONBOARDING_PATHS,
  ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH,
  ONBOARDING_TRADING_METHOD_PATH,
  ONBOARDING_VERIFY_EMAIL_PATH,
  PATH_TO_FEATURE_FLAG,
  SECURITY_SETTINGS_PATH,
  SETTINGS_PATH,
  SIGNAL_EXTRA_DETAILS_SUFFIX,
  TELEGRAM_DISCONNECT_CONFIRMED_PATH,
  TRADE_EXTRA_DETAILS_SUFFIX,
  USER_SIGNALS_PATH,
  VALIDATE_TELEGRAM_PATH,
  PORTFOLIO_ACCOUNTS_PATH,
  ADMIN_PATH,
  ADMIN_SIGNALS_PATH,
  ONBOARDING_SELECT_EXCHANGE_PATH,
  ONBOARDING_CONNECT_EXCHANGE_PATH,
  IS_TELEGRAM_WEB_APP,
  QUICK_ONBOARDING_SUFFIX,
  QUICK_ONBOARDING_PATH,
  SIGNAL_EXTRA_DETAILS_PATH,
} from "common/constants";
import { FixMeLater } from "types";
import { openNotification } from "Global/notifications";
import RedirectWithQuery from "common/Routes/RedirectWithQuery";
import { FILTER_TYPES_DATA } from "@shared/features/filters/constants";
import {
  AuthUser,
  BotType,
  FilterType,
  TelegramWebappQueryData,
  WorkspaceType,
} from "@shared/types";
import { NO_SUBSCRIPTION_WARNING_MESSAGE } from "common/Home/constants";
import { openMessage } from "utils/misc";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import localStorageService from "utils/localStorageService";
import {
  CONNECT_TELEGRAM_KEY,
  ONBOARDING_STEP_KEY,
} from "@shared/features/onboarding/constants";
import { getOnboardingLocalStorageValue } from "features/onboarding/common/utils";
import {
  BINANCE_AUTH_PATH,
  BITGET_AUTH_PATH,
  BOT_TYPE_TO_QUERY_BOT_TYPE,
  BYBIT_AUTH_PATH,
  ENV_TYPE_QUERY_PARAM,
  KUCOIN_AUTH_PATH,
  OKX_AUTH_PATH,
  BINGX_AUTH_PATH,
  REGISTRATION_REF_URL_KEY,
  SIGNAL_EXTRA_DETAILS_TABS,
  SIGNAL_URL_KEY,
  SUB_GROUP_URL_KEY,
} from "@shared/common/constants";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import {
  APPLIED_BACKTESTING_QUERY_PARAM,
  BACKTESTING_BOT_QUERY_PARAM,
  BOT_RANK_QUERY_PARAM,
  NEW_BOT_ID_QUERY_PARAM,
} from "features/bots/common/constants";
import { getStoreWorkspace } from "@shared/common/utils";
import useQuery from "utils/hooks/useQuery";
import {
  QUICK_ONBOARDING_KEY,
  TELEGRAM_VIEW_SIGNAL_KEY,
} from "features/quickOnboarding/constants";

const removeTrailingSlash = (location: string) => {
  return location.replace(/\/$/, "");
};

const getRoute = (
  location: string,
  path: string,
  params: Record<string, number | string | undefined>
) => {
  const baseLocation = getRouteWithoutPath(location, path);
  return removeTrailingSlash(baseLocation) + generatePath(path, params);
};

export const getCoinExtraDetailsRoute = (
  location: string,
  coin: string,
  tab?: string
) => {
  return getRoute(location, COIN_EXTRA_DETAILS_SUFFIX, { coin, tab });
};

export const getAccountExtraDetailsRoute = (
  location: string,
  accountId: number,
  tab?: string
) => {
  return getRoute(location, ACCOUNT_EXTRA_DETAILS_SUFFIX, {
    accountId,
    tab,
  });
};

export const getBotExtraDetailsRoute = (
  location: string,
  botId: string | number,
  tab?: string
) => {
  return getRoute(location, BOT_EXTRA_DETAILS_SUFFIX, {
    botId,
    tab,
  });
};

export const getBacktetingExtraDetailsRoute = (
  location: string,
  botId: string | number,
  tab?: string
) => {
  return getRoute(location, BACKTESTING_EXTRA_DETAILS_SUFFIX, {
    botId,
    tab,
  });
};

export const getQuickOnboardingRoute = (location: string) => {
  return getRoute(location, QUICK_ONBOARDING_SUFFIX, {});
};

export const getGroupExtraDetailsRoute = (
  location: string,
  subGroupId: string,
  tab?: string
) =>
  getRoute(location, MARKETPLACE_GROUP_SUFFIX, {
    subGroupId,
    tab,
  });

export const getBotFormBacktestingQuery = (
  backtestBotId: number,
  backtestId: number
) => {
  return {
    [BACKTESTING_BOT_QUERY_PARAM]: backtestBotId.toString(),
    [APPLIED_BACKTESTING_QUERY_PARAM]: backtestId.toString(),
  };
};

export const getBotFormBacktestingRoute = (
  location: string,
  backtestBotId: number,
  backtestId: number
) => {
  const query = getBotFormBacktestingQuery(backtestBotId, backtestId);
  const updatedQuery = new URLSearchParams();
  Object.entries(query).forEach(([queryKey, value]) => {
    updatedQuery.set(queryKey, value);
  });
  return `${location}?${updatedQuery.toString()}`;
};

export const getSignalsRoute = (subGroupId: number, isAdmin: boolean) => {
  const signalsRoute = isAdmin ? ADMIN_SIGNALS_PATH : USER_SIGNALS_PATH;
  return appendSubGroupId(signalsRoute, subGroupId);
};

export const getBotFormRoute = (
  botType: BotType,
  botFormType:
    | BotFormType.CREATE
    | BotFormType.EDIT
    | BotFormType.DUPLICATE
    | BotFormType.BULK_CREATE,
  botId?: number,
  isChannelConfiguration?: boolean,
  backtestId?: number
) => {
  const baseRoute = generatePath(
    isChannelConfiguration
      ? ADMIN_TRADING_CONFIGURATION_FORM_PATH
      : BASE_BOTS_WITH_ACTION_PATH,
    {
      botId,
      action: botFormType,
      botType: BOT_TYPE_TO_QUERY_BOT_TYPE[botType],
    }
  );
  if (backtestId) {
    const updatedQuery = new URLSearchParams();
    updatedQuery.set(APPLIED_BACKTESTING_QUERY_PARAM, backtestId.toString());
    return `${baseRoute}?${updatedQuery.toString()}`;
  }
  return baseRoute;
};

export const getPublicBotPath = (
  botType: BotType,
  botUuid: string,
  botRank?: number,
  addWorkspace?: boolean
) => {
  let path = generatePath(BASE_PUBLIC_BOT_PATH, {
    botType: BOT_TYPE_TO_QUERY_BOT_TYPE[botType],
    botUUID: botUuid,
  });
  const searchParams = new URLSearchParams();
  if (botRank !== undefined) {
    searchParams.set(BOT_RANK_QUERY_PARAM, botRank.toString());
  }
  if (addWorkspace && getStoreWorkspace() === WorkspaceType.DEMO) {
    searchParams.set(ENV_TYPE_QUERY_PARAM, WorkspaceType.DEMO);
  }
  const searchParamsStr = searchParams.toString();
  if (searchParamsStr) {
    path += `?${searchParamsStr}`;
  }
  return path;
};

export const getPublicBotEditPath = (botType: BotType, botUuid: string) => {
  return generatePath(BASE_PUBLIC_EDIT_BOT_PATH, {
    botType: BOT_TYPE_TO_QUERY_BOT_TYPE[botType],
    botUUID: botUuid,
  });
};

export const getEditBotRoute = (botType: BotType, botId?: number) => {
  return getBotFormRoute(botType, BotFormType.EDIT, botId);
};

export const getDuplicateBotRoute = (botType: BotType, botId: number) => {
  return getBotFormRoute(botType, BotFormType.DUPLICATE, botId);
};

export const getCreateBotRoute = (botType: BotType, isBulk?: boolean) => {
  return getBotFormRoute(
    botType,
    isBulk ? BotFormType.BULK_CREATE : BotFormType.CREATE
  );
};

export const getTradeExtraDetailsRoute = (
  location: string,
  tradeId: number,
  tab?: string,
  isEdit?: boolean
) => {
  return (
    removeTrailingSlash(location) +
    generatePath(TRADE_EXTRA_DETAILS_SUFFIX, {
      tradeId,
      tab,
      edit: isEdit ? "edit" : undefined,
    })
  );
};
export const getSignalExtraDetailsRoute = (
  location: string,
  signalId: number,
  tab: string,
  isEdit?: boolean
) => {
  return (
    removeTrailingSlash(location) +
    generatePath(SIGNAL_EXTRA_DETAILS_SUFFIX, {
      signalId,
      tab,
      edit: isEdit ? "edit" : undefined,
    })
  );
};

export const appendSubGroupId = (path: string, subGroupId: number) => {
  const query = new URLSearchParams();
  query.set(
    FILTER_TYPES_DATA[FilterType.GROUPS].queryKey,
    JSON.stringify([subGroupId])
  );
  return `${path}?${query.toString()}`;
};

export const getGroupManagementRoute = (tab?: string, subGroupId?: number) => {
  const path = generatePath(GROUP_MANAGEMENT_PATH, {
    tab: tab || GROUP_MANAGEMENT_TABS.USERS.toLowerCase(),
  });
  return subGroupId ? appendSubGroupId(path, subGroupId) : path;
};

export const getRouteWithoutPath = (location: string, pathToRemove: string) => {
  const match: FixMeLater = matchPath(location, {
    path: pathToRemove,
    exact: false,
    strict: false,
  });
  return match?.params?.[0] ?? location;
};

const isInPaths = (location: Location, paths: string[]) => {
  for (let path of paths) {
    if (
      matchPath(location.pathname, {
        path,
        exact: false,
        strict: false,
      })
    ) {
      return true;
    }
  }
  return false;
};

export const isBypassPath = (location: Location): boolean => {
  return isInPaths(location, [SETTINGS_PATH, AFFILIATION_PATH, ADMIN_PATH]);
};

export const isOnboardingPath = (location: Location): boolean => {
  return isInPaths(location, ONBOARDING_PATHS);
};

const checkOnboardingRedirects = (
  user: AuthUser,
  testPath: (path: string, exact?: boolean) => boolean,
  query: URLSearchParams
) => {
  const bypassPaths = [SETTINGS_PATH, AFFILIATION_PATH];
  if (user.is_admin) {
    bypassPaths.push(ADMIN_PATH);
  }
  if (user.isExpired() || bypassPaths.some((path) => testPath(path, false))) {
    return null;
  }

  const isOnboardingPath = ONBOARDING_PATHS.some((path) =>
    testPath(path, false)
  );
  const step = getOnboardingLocalStorageValue()?.[user.id];
  const queryExchange = query.get("exchange");

  if (
    isOnboardingPath &&
    (!user.is_onboarding || step === OnboardingDetailedStep.SKIP)
  )
    return <RedirectWithQuery to={"/"} />;

  if (user.is_onboarding && !query.get(NEW_BOT_ID_QUERY_PARAM)) {
    if (!user.is_email_verified && user.email) {
      if (!testPath(ONBOARDING_VERIFY_EMAIL_PATH)) {
        return <RedirectWithQuery to={ONBOARDING_VERIFY_EMAIL_PATH} />;
      }
    } else if (
      (!user.has_clients_connected || testPath(BITGET_AUTH_PATH)) &&
      !testPath(ONBOARDING_SELECT_EXCHANGE_PATH) &&
      (!testPath(ONBOARDING_CONNECT_EXCHANGE_PATH) || queryExchange === null)
    ) {
      return <RedirectWithQuery to={ONBOARDING_SELECT_EXCHANGE_PATH} />;
    } else if (user.has_clients_connected) {
      if (step) {
        if (
          step === OnboardingDetailedStep.COPY_DCA_BOT &&
          !testPath(ONBOARDING_COPY_DCA_BOT_PATH)
        )
          return <RedirectWithQuery to={ONBOARDING_COPY_DCA_BOT_PATH} />;
        else if (
          step === OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP &&
          !testPath(ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH)
        )
          return (
            <RedirectWithQuery to={ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH} />
          );
        else if (
          step === OnboardingDetailedStep.CONNECT_EXTERNAL_ACCOUNT &&
          !testPath(ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH)
        )
          return (
            <RedirectWithQuery to={ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH} />
          );
        else if (
          step === OnboardingDetailedStep.SIGNALS_GROUP_OVERVIEW &&
          !testPath(ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH)
        )
          return (
            <RedirectWithQuery to={ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH} />
          );
        else if (
          step === OnboardingDetailedStep.DCA_BOT_OVERVIEW &&
          !testPath(ONBOARDING_DCA_BOT_OVERVIEW_PATH)
        )
          return <RedirectWithQuery to={ONBOARDING_DCA_BOT_OVERVIEW_PATH} />;
        else if (
          (OnboardingDetailedStep.TRADING_METHOD === step ||
            (IS_TELEGRAM_WEB_APP &&
              [
                OnboardingDetailedStep.CONNECT_EXCHANGE,
                OnboardingDetailedStep.SELECT_EXCHANGE,
              ].includes(step))) && // Handle redirect when polling for new accounts in telegram webapps
          !testPath(ONBOARDING_TRADING_METHOD_PATH)
        )
          return <RedirectWithQuery to={ONBOARDING_TRADING_METHOD_PATH} />;
      } else if (!isOnboardingPath) {
        return <RedirectWithQuery to={ONBOARDING_TRADING_METHOD_PATH} />;
      }
    }
  }
};

const checkConnectTelegramRedirects = (
  user: AuthUser,
  testPath: (path: string, exact?: boolean) => boolean,
  query: URLSearchParams
) => {
  if (
    !user.is_telegram_connected &&
    ((testPath(CONNECT_TELEGRAM_PATH) && query.get("short_uuid")) ||
      (testPath(VALIDATE_TELEGRAM_PATH) &&
        query.get("short_uuid") &&
        query.get("sec_code")))
  ) {
    if (
      user.is_onboarding &&
      !localStorageService.getValue(ONBOARDING_STEP_KEY) &&
      !testPath(ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH)
    ) {
      return (
        <RedirectWithQuery to={ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH} />
      );
    } else if (
      !user.is_onboarding &&
      !localStorageService.getValue(CONNECT_TELEGRAM_KEY) &&
      !testPath(SECURITY_SETTINGS_PATH)
    ) {
      return <RedirectWithQuery to={SECURITY_SETTINGS_PATH} />;
    }
  }

  const connectTelegramOriginPath =
    localStorageService.getValue(CONNECT_TELEGRAM_KEY);

  if (connectTelegramOriginPath) {
    if (user.is_onboarding) {
      localStorageService.removeValue(CONNECT_TELEGRAM_KEY);
    } else {
      const parsedValue = JSON.parse(connectTelegramOriginPath);
      if (!testPath(parsedValue.pathname)) {
        return (
          <RedirectWithQuery
            to={`${parsedValue.pathname}${parsedValue.search}`}
          />
        );
      }
    }
  }

  if (
    testPath(TELEGRAM_DISCONNECT_CONFIRMED_PATH) ||
    testPath(DISCORD_DISCONNECT_CONFIRMED_PATH)
  ) {
    return <RedirectWithQuery to={SECURITY_SETTINGS_PATH} />;
  }
};

export const checkQuickConnectRedirects = (pathname: string) => {
  return [
    BINANCE_AUTH_PATH,
    BYBIT_AUTH_PATH,
    OKX_AUTH_PATH,
    BITGET_AUTH_PATH,
    KUCOIN_AUTH_PATH,
    BINGX_AUTH_PATH,
  ].some((path) =>
    matchPath(pathname, {
      path,
      exact: true,
      strict: false,
    })
  );
};

const checkExternalLinksRedirects = (
  user: AuthUser,
  location: Location,
  query: URLSearchParams
) => {
  const _matchPath = (path: string, exact = true) =>
    matchPath(location.pathname, {
      path,
      exact,
      strict: false,
    });
  const accountConfigurationMatch = _matchPath(
    EXTERNAL_ACCOUNT_CONFIGURATION_PATH
  ) as { params?: { accountId?: string } };
  if (accountConfigurationMatch?.params?.accountId) {
    const accountConfigurationPath = getAccountExtraDetailsRoute(
      PORTFOLIO_ACCOUNTS_PATH,
      Number(accountConfigurationMatch.params.accountId)
    );
    return <RedirectWithQuery to={accountConfigurationPath} />;
  }

  if (checkQuickConnectRedirects(location.pathname)) {
    if (user.is_onboarding) {
      return <RedirectWithQuery to={ONBOARDING_CONNECT_EXCHANGE_PATH} />;
    } else return <RedirectWithQuery to={CREATE_ACCOUNT_PATH} />;
  }

  if (_matchPath(OAUTH_LOGIN_PATH)) {
    return <RedirectWithQuery to={LOGIN_PATH} />;
  }
  if (_matchPath(DISCORD_FOLLOW_SIGNAL_PATH)) {
    const { signalId, subGroupId } = getSignalFollowData(query);
    if (signalId && subGroupId) {
      if (user.is_discord_connected) {
        const signalDetailsPath = appendSubGroupId(
          getSignalExtraDetailsRoute(
            USER_SIGNALS_PATH,
            parseInt(signalId),
            SIGNAL_EXTRA_DETAILS_TABS.GENERAL,
            false
          ),
          parseInt(subGroupId)
        );
        return <Redirect to={signalDetailsPath} />;
      } else {
        return <RedirectWithQuery to={SECURITY_SETTINGS_PATH} />;
      }
    } else {
      return <Redirect to={"/"} />;
    }
  }
};

const checkQuickOnboardingRedirect = (
  user: AuthUser | undefined,
  location: Location,
  testPath: (path: string, exact?: boolean) => boolean,
  getHomePath: () => string,
  telegramStartValue?: TelegramWebappQueryData,
  isRedirected?: boolean,
  workspace?: WorkspaceType
) => {
  const isQuickOnboardingPath = testPath(QUICK_ONBOARDING_PATH);
  const isChannel = telegramStartValue?.parameters?.source === "channel";
  const isViewSignal =
    isChannel &&
    telegramStartValue?.path &&
    !!matchPath(telegramStartValue?.path, { path: SIGNAL_EXTRA_DETAILS_PATH });
  const isViewSignalSecondClick =
    localStorageService.getValue(TELEGRAM_VIEW_SIGNAL_KEY) === "true";
  const quickOnboardingViewed =
    localStorageService.getValue(QUICK_ONBOARDING_KEY) === "true";
  const shouldShowQuickOnboarding =
    workspace === WorkspaceType.USER &&
    !user?.is_onboarding &&
    !user?.has_clients_connected;

  const showQuickOnboarding =
    shouldShowQuickOnboarding &&
    !isQuickOnboardingPath &&
    !quickOnboardingViewed &&
    (!isChannel || !isViewSignal || isViewSignalSecondClick);

  if (isViewSignal && !isViewSignalSecondClick && shouldShowQuickOnboarding) {
    localStorageService.setValue(TELEGRAM_VIEW_SIGNAL_KEY, true);
    return null;
  } else if (showQuickOnboarding && !isRedirected) {
    const pathname = testPath("/", true) ? getHomePath() : location.pathname;
    return <RedirectWithQuery to={getQuickOnboardingRoute(pathname)} />;
  }
};

export const checkAppRedirects = (
  user: AuthUser,
  location: Location,
  query: URLSearchParams,
  getValidWorkspacesByPath: (pathname: string) => WorkspaceType[],
  getHomePath: () => string,
  telegramStartValue?: TelegramWebappQueryData,
  isRedirected?: boolean,
  workspace?: WorkspaceType
) => {
  const testPath = (path: string, exact = true) =>
    !!matchPath(location.pathname, {
      path,
      exact,
      strict: false,
    });

  for (let route of Object.keys(PATH_TO_FEATURE_FLAG)) {
    const featureFlag = PATH_TO_FEATURE_FLAG[route];
    if (testPath(route, false) && !user.hasFeatureFlag(featureFlag)) {
      return <Redirect to={"/"} />;
    }
  }

  const externalLinkRedirect = checkExternalLinksRedirects(
    user,
    location,
    query
  );
  if (externalLinkRedirect) return externalLinkRedirect;
  const connectTelegramRedirect = checkConnectTelegramRedirects(
    user,
    testPath,
    query
  );
  if (connectTelegramRedirect) return connectTelegramRedirect;
  const onboardingRedirect = checkOnboardingRedirects(user, testPath, query);
  if (onboardingRedirect) return onboardingRedirect;

  const quickOnboardingRedirect = checkQuickOnboardingRedirect(
    user,
    location,
    testPath,
    getHomePath,
    telegramStartValue,
    isRedirected,
    workspace
  );
  if (quickOnboardingRedirect) return quickOnboardingRedirect;

  const paymentStatusPaths = [
    BASE_CANCELLED_AGREEMENT_PATH,
    BASE_PROCESSED_AGREEMENT_PATH,
    BASE_FAILED_AGREEMENT_PATH,
    BASE_PAID_AGREEMENT_PATH,
  ];
  for (let route of paymentStatusPaths) {
    if (testPath(route, false)) {
      return (
        <RedirectWithQuery
          to={
            BASE_BILLING_SETTINGS_PATH +
            location.pathname +
            (location.search || "")
          }
        />
      );
    }
  }

  if (
    !user.is_onboarding &&
    !ONBOARDING_PATHS.some((path) => testPath(path, false))
  ) {
    const nextPath = query.get(NEXT_URL_QUERY_PARAM);
    if (nextPath) {
      return (
        <RedirectWithQuery
          queryKeysToDelete={[NEXT_URL_QUERY_PARAM]}
          to={nextPath}
        />
      );
    }

    if (!getValidWorkspacesByPath(location.pathname).length) {
      if (user.is_admin && user.isLimitedAccess()) {
        openMessage(NO_SUBSCRIPTION_WARNING_MESSAGE, "warning");
        return <Redirect to={BILLING_SETTINGS_PATH} />;
      } else {
        return <Redirect to={"/"} />;
      }
    }
  }

  const limitedAccessPaths = [SETTINGS_PATH, AFFILIATION_PATH];
  if (user.is_admin) {
    limitedAccessPaths.push(ADMIN_PATH);
    limitedAccessPaths.push(ADMIN_SIGNALS_PATH);
  }
  if (
    user.isLimitedAccess() &&
    !limitedAccessPaths.some((path) => testPath(path, false))
  ) {
    const redirectPath = user.is_admin
      ? GROUP_MANAGEMENT_PATH
      : BASE_BILLING_SETTINGS_PATH;
    let title = "";
    let message = "";
    if (!user.is_admin) {
      if (user.isExpired()) {
        title = "Subscription Expired";
        message =
          "Your subscription to Cornix has ended. In order to keep using our service, please renew your subscription.";
      } else if (!user.isPro()) {
        title = "Upgrade Subscription";
        message =
          "Your current plan does not include access to the website. To use the website, please upgrade your subscription.";
      }
    }
    if (message) {
      openNotification(message, title, "warning", 0);
    }

    return <RedirectWithQuery to={redirectPath} />;
  }
};

export const getSignalFollowData = (query: URLSearchParams) => {
  const decodeValue = (value?: string | null) => atob(value || "");
  const actionAndSignalId = decodeValue(query.get(SIGNAL_URL_KEY));
  const subGroupId = decodeValue(query.get(SUB_GROUP_URL_KEY));
  const ref = decodeValue(query.get(REGISTRATION_REF_URL_KEY));
  const actionAndSignalIdMatch = actionAndSignalId?.match(
    new RegExp("(\\w*)-(\\d+)")
  );
  const signalId = actionAndSignalIdMatch?.[2];
  return {
    actionAndSignalId,
    subGroupId,
    signalId,
    ref,
  };
};

export const getCreateChannelConfigurationsPath = () => {
  return getBotFormRoute(BotType.SIGNAL, BotFormType.CREATE, undefined, true);
};

export const getRouteWithExchange = (
  primaryExchange: string,
  location: Location
) => {
  const updatedQuery = new URLSearchParams();
  updatedQuery.set("exchange", primaryExchange);
  return `${location.pathname}?${updatedQuery.toString()}`;
};

export const getTelegramWebAppPath = (botLink: string, path?: string) => {
  const updatedQuery = new URLSearchParams();
  let param = "";
  if (path) {
    try {
      param = btoa(JSON.stringify({ path }));
    } catch {}
  }
  updatedQuery.set("startapp", param);
  return `${botLink}/cornix?${updatedQuery.toString()}`;
};

export const useQueryGroupId = () => {
  const query = useQuery();
  const rawData = query.get(FILTER_TYPES_DATA[FilterType.GROUPS].queryKey);
  if (rawData) {
    try {
      return JSON.parse(rawData)?.[0];
    } catch {}
  }
};
